import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
  useRef,
} from "react";

import {
  Users,
  AlertCircle,
  Check,
} from "lucide-react";

import { ThemeContext } from "../../constant/theme";
import { LanguageContext } from "../../constant/LanguageContext";
import { apiFetch, useAuth } from "../../context/AuthContext";
import { useProfile } from "../../hooks/useProfile";

import employeeActionsAPI from "./utils/changesAPIs";
import ReusableDropdown from "../../components/ReusableDropdown";
import { getEnglishInitials } from "../../components/UserAvatar";

import translations from "./Constant/EmployeeActions.translations";
import createStyles from "./Style/EmployeeActions.styles";
import {
  ACTION_TYPES,
  ACTION_FEATURE_MAP,
  getWorkflowApprovalLabel,
} from "./Constant/EmployeeActions.config";
import { generateStatsData, getDefaultStatsData } from "./Constant/EmployeeActions.stats";
import {
  normalizeAdminLevelPermissions,
  normalizePermissionFeatureName,
} from "../../utils/permissionNormalization";

import ManagerAutocomplete from "./components/ManagerAutocomplete";
import CustomActionDropdown from "./components/Customactiondropdown";
import ActionFormFields from "./components/ActionFormFields";
import SharedStatsGrid from "../../components/StatsGrid";
import EmployeeTable from "./components/EmployeeTable";
import { SkeletonStyles, SkeletonBox, SkeletonStatCard, LoadingPill } from "../../components/SkeletonLoader";
import ActionModal from "./components/ActionModal";
import ConfirmModal from "./components/ConfirmModal";
import HistoryModal from "./components/HistoryModal";
import PendingApprovalsModal from "./components/PendingApprovalsModal";
import EmployeeHistoryModal from "./components/EmployeeHistoryModal";
import RejectModal from "./components/RejectModal";

// ─── Initial form state helper ────────────────────────────────────────────────
const EMPTY_FORM = {
  newPosition: "",
  newDepartment: "",
  newSalary: "",
  newGrossSalary: "",
  salaryInputSource: "",
  effectiveDate: "",
  reason: "",
  approverNotes: "",
  reportsTo: "",
  performanceRating: "",
  terminationType: "",
  terminationReason: "",
  lastWorkingDay: "",
  severanceAmount: "",
  eligibleForRehire: "yes",
  suspensionStartDate: "",
  suspensionEndDate: "",
  suspensionType: "",
  suspensionReason: "",
  trainingProgram: "",
  trainingStartDate: "",
  trainingEndDate: "",
  trainingCost: "",
  trainingObjectives: "",
  reviewScore: "",
  strengths: "",
  improvements: "",
  nextReviewDate: "",
  contractType: "",
  contractStartDate: "",
  contractEndDate: "",
  contractTerms: "",
  rotationDepartment: "",
  rotationRole: "",
  rotationDuration: "",
  rotationObjectives: "",
  priority: "medium",
  confidential: false,
  notifyEmployee: true,
  notifyManager: true,
  attachments: [],
};

// ─── Main Component ────────────────────────────────────────────────────────────
const EmployeeActionsPage = () => {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const { profile } = useProfile();
  const { hasModuleAccess, hasFeatureAccess, isColumnVisible } = useAuth();

  const t = translations[language] || translations.ar;
  const isRTL = language === "ar";
  const styles = useMemo(() => createStyles(theme, isRTL), [theme, isRTL]);

  const viewType =
    localStorage.getItem("managerViewType") ||
    localStorage.getItem("viewType") ||
    "manager";
  const isAdminView = viewType === "admin";

  // ── State ──────────────────────────────────────────────────────────────────
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("all");
  const [filterCompany, setFilterCompany] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("all");
  const [filterPerformance, setFilterPerformance] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [actionHistory, setActionHistory] = useState([]);
  const [pendingActions, setPendingActions] = useState([]);
  const [permissions, setPermissions] = useState({ modules: [], features: {} });
  const [userRole, setUserRole] = useState(
    profile?.role || localStorage.getItem("role") || ""
  );
  const isAdminRole =
    userRole === "admin" || userRole === "superadmin";

  // Admin-level feature gate (registry: changesPage.viewpendingapprovals).
  // Only constrains promoted users in Admin View — manager view keeps the
  // approval workflow; admin/superadmin bypass inside hasFeatureAccess.
  const canViewPendingApprovals =
    !isAdminView ||
    isAdminRole ||
    hasFeatureAccess("changesPage", "viewpendingapprovals");

  const [stats, setStats] = useState([]);

  // Modal visibility
  const [showActionModal, setShowActionModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showPendingModal, setShowPendingModal] = useState(false);
  // Notification deep-link: the pending action to highlight inside the modal.
  const [highlightActionId, setHighlightActionId] = useState(null);
  const [showEmployeeHistoryModal, setShowEmployeeHistoryModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);

  // Selected data
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedAction, setSelectedAction] = useState("");
  const [pendingAction, setPendingAction] = useState(null);
  const [selectedEmployeeHistory, setSelectedEmployeeHistory] = useState(null);
  const [employeeHistoryData, setEmployeeHistoryData] = useState([]);

  // Rejection
  const [rejectingActionId, setRejectingActionId] = useState(null);
  const [rejectingActionData, setRejectingActionData] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");

  // UI
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [hoveredActionBtn, setHoveredActionBtn] = useState(null);
  const [employeeImages, setEmployeeImages] = useState({});
  const [pendingWorkflows, setPendingWorkflows] = useState({});
  const [workflowDefinitions, setWorkflowDefinitions] = useState([]);
  // Authoritative department list — always sourced from GET /api/departments
  // (the same source of truth the Departments admin page manages), never
  // derived from whichever employees happen to be loaded on screen. See
  // fetchDepartments below.
  const [departments, setDepartments] = useState([]);
  // Authoritative manager list, sourced from the SAME /api/departments
  // response as `departments` above (each department row already carries its
  // real assigned managers — the same DepartmentManager rows the workflow
  // engine's DEPARTMENT_MANAGER step type resolves against). Previously
  // ManagerAutocomplete derived its own manager list by guessing from
  // whichever employees happened to be loaded on screen (anyone who was
  // someone's firstLineManager), which drifted from the real,
  // department-assigned managers and from the `departments` list above —
  // this keeps both in sync from one source of truth.
  const [departmentManagers, setDepartmentManagers] = useState([]);

  const [formData, setFormData] = useState(EMPTY_FORM);

  const debounceTimerRef = useRef(null);

  // ── Skeleton فقط في أول تحميل — مش هيظهر تاني مع كل ريفريش ─────────────────
  const [pageReady, setPageReady] = useState(false);
  useEffect(() => {
    if (!isLoading) setPageReady(true);
  }, [isLoading]);

  // ── Permissions ────────────────────────────────────────────────────────────
  useEffect(() => {
    setUserRole(profile?.role || localStorage.getItem("role") || "");
    if (!isAdminView) return;

    const fetchPermissions = async () => {
      try {
        const client =
          profile?.workingCompany ||
          localStorage.getItem("workingCompany") ||
          "";
        if (!client) { setPermissions({ modules: [], features: {} }); return; }

        const BASE = process.env.REACT_APP_BASE_URL || "";
        const res = await apiFetch(
          `${BASE}/api/admin-levels/resolve/permissions?workingCompany=${encodeURIComponent(client)}`,
          { method: "GET" }
        );
        if (!res.ok) { setPermissions({ modules: [], features: {} }); return; }
        const json = await res.json();
        setPermissions(
          normalizeAdminLevelPermissions(
            json?.permissions || json?.data || json || { modules: [], features: {} }
          )
        );
      } catch {
        setPermissions({ modules: [], features: {} });
      }
    };

    fetchPermissions();
  }, [isAdminView]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const getInitials = useCallback(
    (name, englishName = null) => getEnglishInitials(name, englishName, "US"),
    []
  );

  const getEmployeeName = useCallback(
    (emp) =>
      language === "ar"
        ? emp.arabicName || emp.sr || t.notAvailable
        : emp.sr || emp.arabicName || t.notAvailable,
    [language, t]
  );

  // ── Fetch helpers ──────────────────────────────────────────────────────────
  const fetchClients = useCallback(async () => {
    try {
      const data = await employeeActionsAPI.getAllClients();
      setClients(Array.isArray(data) ? data : []);
    } catch { setClients([]); }
  }, []);

  // Fetch the real department list from the departments API — this is the
  // same table the Departments admin page reads/writes, so a rename, merge,
  // or newly created (still-empty) department is reflected here too, instead
  // of only whatever department names happen to appear on already-loaded
  // employee rows.
  const fetchDepartments = useCallback(async () => {
    try {
      const clientName =
        profile?.clientName || localStorage.getItem("clientName");
      const workingCompany =
        profile?.workingCompany || localStorage.getItem("workingCompany");
      if (!clientName) { setDepartments([]); setDepartmentManagers([]); return; }

      const BASE = process.env.REACT_APP_BASE_URL || "";
      const params = new URLSearchParams({ clientName });
      if (workingCompany) params.set("workingCompany", workingCompany);

      const res = await apiFetch(
        `${BASE}/api/departments?${params.toString()}`,
        { method: "GET" }
      );
      if (!res.ok) { setDepartments([]); setDepartmentManagers([]); return; }
      const json = await res.json();
      const list = Array.isArray(json?.data) ? json.data : [];
      const activeList = list.filter((d) => d.isActive !== false);

      setDepartments(
        activeList
          .map((d) => d.name)
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b))
      );

      // Fetch all active company employees so the manager dropdown list contains all company employees
      let assignableEmployees = [];
      try {
        assignableEmployees = await employeeActionsAPI.listWorkflowAssignableEmployees(
          clientName,
          workingCompany
        );
      } catch {
        assignableEmployees = [];
      }

      const managersByCode = new Map();

      // Populate with all active company employees
      if (Array.isArray(assignableEmployees)) {
        assignableEmployees.forEach((emp) => {
          if (!emp?.infinityCode) return;
          const deptNames = emp.departmentNames && emp.departmentNames.length > 0
            ? emp.departmentNames
            : emp.departmentName
              ? [emp.departmentName]
              : emp.employeeType
                ? [emp.employeeType]
                : [];
          managersByCode.set(emp.infinityCode, {
            infinityCode: emp.infinityCode,
            clientCode: emp.clientCode || emp.infinityCode,
            arabicName: emp.arabicName || null,
            sr: emp.sr || emp.name || null,
            role: emp.role || null,
            departmentNames: deptNames,
          });
        });
      }

      // Merge department-assigned manager details if available
      activeList.forEach((dept) => {
        (Array.isArray(dept.managers) ? dept.managers : []).forEach((m) => {
          if (!m?.infinityCode) return;
          const existing = managersByCode.get(m.infinityCode);
          const departmentNames = existing?.departmentNames ? [...existing.departmentNames] : [];
          if (dept.name && !departmentNames.includes(dept.name)) departmentNames.push(dept.name);
          managersByCode.set(m.infinityCode, {
            infinityCode: m.infinityCode,
            clientCode: m.employee?.clientCode || existing?.clientCode || m.infinityCode,
            arabicName: m.employee?.arabicName || existing?.arabicName || null,
            sr: m.employee?.sr || existing?.sr || null,
            role: m.role || existing?.role || null,
            departmentNames,
          });
        });
      });

      setDepartmentManagers(
        [...managersByCode.values()].sort((a, b) =>
          (a.sr || a.arabicName || a.infinityCode).localeCompare(b.sr || b.arabicName || b.infinityCode)
        )
      );
    } catch {
      setDepartments([]);
      setDepartmentManagers([]);
    }
  }, [profile]);

  const fetchActionHistory = useCallback(async () => {
    try {
      // Same viewType/clientName the server uses to scope this list to the
      // caller (manager view -> their own hierarchy, admin view -> their
      // admin-level visibility set) — without it a manager in manager view
      // would see history for employees who aren't theirs at all.
      const clientName =
        profile?.clientName || localStorage.getItem("clientName");
      const data = await employeeActionsAPI.getAllActions({
        viewType: isAdminView ? "admin" : "manager",
        clientName,
      });
      setActionHistory(Array.isArray(data) ? data : []);
    } catch { setActionHistory([]); }
  }, [isAdminView, profile]);

  const fetchPendingActions = useCallback(async () => {
    try {
      const viewRole = isAdminView ? "admin" : "manager";
      const options = {};

      if (selectedClient && selectedClient !== "all")
        options.clientIdentifier = selectedClient.toLowerCase();

      const workingCompany =
        profile?.workingCompany || localStorage.getItem("workingCompany");
      if (workingCompany) options.workingCompany = workingCompany;

      const adminLevelId = profile?.adminLevelId || profile?.adminLevel?.id || profile?.admin_level_id;
      if (adminLevelId) {
        options.adminLevelId = adminLevelId;
      } else {
        try {
          const userStr =
            localStorage.getItem("user") || sessionStorage.getItem("user");
          if (userStr) {
            const obj = JSON.parse(userStr);
            if (obj?.adminLevelId || obj?.admin_level_id || obj?.adminLevel?.id)
              options.adminLevelId = obj.adminLevelId || obj.admin_level_id || obj.adminLevel?.id;
          }
        } catch { }
      }

      const mgr =
        profile?.infinityCode || localStorage.getItem("infinityCode");
      if (mgr) options.managerInfinityCode = mgr;

      const data = await employeeActionsAPI.getPendingActionsByView(
        viewRole,
        options
      );

      let pendingData = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : data?.actions ?? [];

      // ── Unified workflow fetch (single call, not N+1) ──────────────────────
      // Prefer the new engine; fall back gracefully if it errors.
      try {
        const myCode = profile?.infinityCode || localStorage.getItem("infinityCode");
        const myLevelId =
          profile?.adminLevelId ||
          profile?.adminLevel?.id ||
          profile?.admin_level_id ||
          (() => {
            try {
              const raw = localStorage.getItem("adminLevel");
              if (raw) {
                const obj = JSON.parse(raw);
                return obj?.adminLevelId || obj?.admin_level_id || obj?.id || obj?.adminLevel?.id || null;
              }
            } catch {}
            return null;
          })();

        if (myCode) {
          // Single call returns WorkflowInstanceStep[] for this user, module=ACTION
          const pendingSteps = await employeeActionsAPI.getPendingApprovals(
            myCode, myLevelId, "ACTION"
          );

          // Key steps by recordId (= employeeAction.id) so the UI can look them up.
          // Store both the numeric and string form to avoid mismatches from the API.
          const wfMap = {};
          (Array.isArray(pendingSteps) ? pendingSteps : []).forEach((step) => {
            const rid = step?.workflowInstance?.recordId ?? step?.recordId;
            if (rid != null) {
              const key = String(rid);
              if (!wfMap[key]) wfMap[key] = [];
              wfMap[key].push(step);
              if (rid !== key) wfMap[rid] = wfMap[key];
            }
          });
          setPendingWorkflows(wfMap);

          // ── THE list is now driven by the workflow engine, not just by
          // employee-visibility ────────────────────────────────────────────
          // `pendingSteps` above is the engine's authoritative answer to
          // "which steps are assigned to me right now" — it already resolves
          // DIRECT_MANAGER / SECOND_MANAGER / DEPARTMENT_MANAGER / EXPLICIT /
          // ADMIN_LEVEL (via assignedToAdminLevelId) / ADMIN_GENERAL
          // correctly. But it was ONLY ever used to populate the
          // approve-button lookup map above; the actual ROWS came solely
          // from getPendingActionsByView(), which decides what you can see
          // by intersecting employee-visibility rules and separately-gated
          // permission endpoints. Those two things are not the same:
          // being the configured APPROVER for a step (workflow routing) does
          // not imply having general employee-VISIBILITY of the subject
          // employee. So a request correctly assigned to an admin level at
          // step 1 could have its step sitting in wfMap while its row was
          // filtered out of the list entirely — approve buttons that render
          // for a row that never appears.
          //
          // Fetch any assigned-to-me action that the visibility-scoped list
          // didn't already include, by id, and union it in. GET
          // /employee-actions/:id only needs changesPage.view (which anyone
          // loading this page already has), so it sidesteps both the
          // visibility intersection and the viewpendingapprovals gate.
          const assignedIds = [
            ...new Set(
              (Array.isArray(pendingSteps) ? pendingSteps : [])
                .map((s) => s?.workflowInstance?.recordId ?? s?.recordId)
                .filter((id) => id != null)
                .map((id) => String(id))
            ),
          ];
          const alreadyListed = new Set(
            pendingData.map((a) => String(a?.id)).filter(Boolean)
          );
          const missingIds = assignedIds.filter((id) => !alreadyListed.has(id));

          if (missingIds.length > 0) {
            const fetched = await Promise.all(
              missingIds.map((id) =>
                employeeActionsAPI.getActionById(id).catch(() => null)
              )
            );
            const extras = fetched.filter(
              (a) =>
                a &&
                a.id != null &&
                String(a.status || "").trim().toLowerCase() === "pending"
            );
            if (extras.length > 0) pendingData = [...pendingData, ...extras];
          }
        } else {
          setPendingWorkflows({});
        }
      } catch {
        // Silently fall back — page is still usable without workflow steps
        setPendingWorkflows({});
      }

      setPendingActions(pendingData);

      setStats((prev) =>
        prev.map((stat, idx) =>
          idx === 5 ? { ...stat, value: pendingData.length } : stat
        )
      );
      return pendingData;
    } catch {
      setPendingActions([]);
      return [];
    }
  }, [isAdminView, selectedClient, profile]);

  const fetchEmployees = useCallback(
    async (showLoader = false) => {
      if (showLoader) setIsLoading(true);
      try {
        const workingCompany =
          profile?.workingCompany || localStorage.getItem("workingCompany");
        const storedClientName =
          profile?.clientName || localStorage.getItem("clientName");
        const myInfinityCode =
          profile?.infinityCode || localStorage.getItem("infinityCode");
        const role = (
          profile?.role || localStorage.getItem("role") || ""
        ).toLowerCase();
        const forceAdminView = role === "admin" || role === "superadmin";
        const storedViewType = (
          localStorage.getItem("managerViewType") ||
          localStorage.getItem("viewType") ||
          "manager"
        ).toLowerCase();
        const resolvedViewType = forceAdminView ? "admin" : storedViewType;

        const resolveClientName = () => {
          if (!selectedClient || selectedClient === "all")
            return storedClientName || workingCompany;
          const sel = selectedClient.toString().toLowerCase();
          const match = clients.find((c) => {
            const id = c.id != null ? c.id.toString().toLowerCase() : null;
            const name = (c.clientName || c.name || "").toLowerCase();
            return (id && id === sel) || name === sel;
          });
          return match ? match.clientName || match.name || selectedClient : selectedClient;
        };

        const resolvedClient = resolveClientName();
        let data;

        if (resolvedViewType === "admin") {
          data = resolvedClient
            ? await employeeActionsAPI.getEmployeesByVisibility(resolvedClient, "admin")
            : await employeeActionsAPI.getEmployeesByVisibility(undefined, "admin");
        } else {
          const targetClient = resolvedClient || workingCompany || storedClientName;
          if (!targetClient) throw new Error("No client identifier available");

          if (!myInfinityCode) {
            if (forceAdminView) {
              data = await employeeActionsAPI.getEmployeesByVisibility(targetClient, "admin");
            } else {
              throw new Error("No manager infinity code");
            }
          } else {
            data = await employeeActionsAPI.getEmployeesByClientbymanager(
              targetClient.toString().trim().toLowerCase(),
              myInfinityCode
            );
            if ((!Array.isArray(data) || data.length === 0) && forceAdminView) {
              data = await employeeActionsAPI.getEmployeesByVisibility(targetClient, "admin");
            }
          }
        }

        setEmployees(Array.isArray(data) ? data : []);
      } catch {
        setErrorMsg(t.errorLoadingEmployees);
        setEmployees([]);
      } finally {
        if (showLoader) setIsLoading(false);
      }
    },
    [selectedClient, filterDepartment, filterPerformance, searchTerm, t, clients]
  );

  const fetchStats = useCallback(async () => {
    try {
      const actionsArray = Array.isArray(actionHistory)
        ? actionHistory
        : actionHistory?.data ?? [];

      const now = new Date();
      const monthly = actionsArray.filter((a) => {
        const d = new Date(a.effectiveDate || a.createdAt || a.requestedDate);
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      });

      const statsData = generateStatsData(
        t,
        language,
        employees,
        monthly,
        pendingActions.length
      );
      setStats(statsData);
    } catch {
      setStats(getDefaultStatsData(t, language));
    }
  }, [employees, pendingActions.length, actionHistory, language, t]);

  // ── Approve / Reject ───────────────────────────────────────────────────────
  /**
   * Approve the current pending step for an action.
   *
   * @param {number} actionId         — EmployeeAction.id
   * @param {object} [actionRecord]   — the action row (to read workflow data)
   */
  const handleApproveAction = useCallback(
    async (actionIdOrAction) => {
      setIsSubmitting(true);
      try {
        const actionId =
          typeof actionIdOrAction === "object"
            ? actionIdOrAction.id
            : actionIdOrAction;
        const myCode = profile?.infinityCode || localStorage.getItem("infinityCode");

        // Resolve "the actual step" server-side (workflowInstance.currentStepOrder)
        // instead of looking it up in the frontend's pendingWorkflows map, which
        // only ever contains steps assigned to *this* viewer — for an admin
        // bypassing a step assigned to someone else, that map has no entry at
        // all. The by-record endpoint also carries the admin/superadmin
        // override (approves every remaining step in one call).
        await employeeActionsAPI.approveActionByRecord(actionId, myCode);

        setSuccessMsg(t.successActionApproved);
        // Approving a step can finalize the action immediately (when the
        // effective date has already arrived, or the approver is an admin),
        // which updates the employee's position/salary/department right
        // away on the backend. Refetch employees (and departments, in case
        // a transfer just filled a department) alongside the
        // approvals/history/stats — otherwise the change is applied but
        // stays invisible until a manual page reload.
        await Promise.all([
          fetchPendingActions(),
          fetchActionHistory(),
          fetchStats(),
          fetchEmployees(false),
          fetchDepartments(),
        ]);
        setTimeout(() => setSuccessMsg(""), 5000);
      } catch {
        setErrorMsg(t.errorApprovingAction);
        setTimeout(() => setErrorMsg(""), 5000);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      fetchPendingActions,
      fetchActionHistory,
      fetchStats,
      fetchEmployees,
      fetchDepartments,
      t,
      profile,
    ]
  );

  const handleRejectAction = useCallback(
    async (actionId, reasonOverride = null) => {
      setIsSubmitting(true);
      try {
        const myCode = profile?.infinityCode || localStorage.getItem("infinityCode");
        const reasonText =
          (reasonOverride && reasonOverride.trim()) ||
          rejectionReason?.trim() ||
          "Rejected by approver";

        // See handleApproveAction above — same reasoning for using the
        // by-record endpoint (server resolves the actual current step,
        // and admins get the override bypass).
        await employeeActionsAPI.rejectActionByRecord(actionId, myCode, reasonText);

        setSuccessMsg(t.successActionRejected);
        setShowRejectModal(false);
        setRejectingActionId(null);
        setRejectingActionData(null);
        setRejectionReason("");

        // Keep employees/departments in sync with every other change too —
        // a rejected action shouldn't leave stale data behind either.
        await Promise.all([
          fetchPendingActions(),
          fetchActionHistory(),
          fetchStats(),
          fetchEmployees(false),
          fetchDepartments(),
        ]);
        setTimeout(() => setSuccessMsg(""), 5000);
      } catch {
        setErrorMsg(t.errorRejectingAction);
        setTimeout(() => setErrorMsg(""), 5000);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      rejectionReason,
      fetchPendingActions,
      fetchActionHistory,
      fetchStats,
      fetchEmployees,
      fetchDepartments,
      t,
      profile,
    ]
  );

  // ── Employee History ───────────────────────────────────────────────────────
  const handleOpenEmployeeHistory = useCallback(async (employee) => {
    setSelectedEmployeeHistory(employee);
    setShowEmployeeHistoryModal(true);
    try {
      const history = await employeeActionsAPI.getEmployeeActions(
        employee.infinityCode
      );
      setEmployeeHistoryData(
        Array.isArray(history)
          ? history.sort(
            (a, b) =>
              new Date(b.effectiveDate || b.createdAt) -
              new Date(a.effectiveDate || a.createdAt)
          )
          : []
      );
    } catch {
      setEmployeeHistoryData([]);
    }
  }, []);

  // ── Submit ─────────────────────────────────────────────────────────────────
  const validateForm = useCallback(() => {
    if (!selectedAction) { setErrorMsg(t.pleaseSelectAction); return false; }
    if (selectedAction === "suspend") {
      if (!formData.suspensionStartDate) { setErrorMsg(t.suspensionStartDateRequired || (language === "ar" ? "تاريخ بدء الإيقاف مطلوب" : "Suspension start date is required")); return false; }
      if (!formData.suspensionEndDate) { setErrorMsg(t.suspensionEndDateRequired || (language === "ar" ? "تاريخ انتهاء الإيقاف مطلوب" : "Suspension end date is required")); return false; }
    } else if (selectedAction === "terminate") {
      if (!formData.lastWorkingDay) { setErrorMsg(language === "ar" ? "تاريخ آخر يوم عمل مطلوب" : "Last working day is required"); return false; }
    } else {
      if (!formData.effectiveDate) { setErrorMsg(t.effectiveDateRequired); return false; }
    }
    if (!formData.reason) { setErrorMsg(t.reasonRequired); return false; }
    if (!formData.approverNotes) { setErrorMsg(t.approverNotesRequired); return false; }
    return true;
  }, [selectedAction, formData, t, language]);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (!validateForm()) return;
      const actionConfig = ACTION_TYPES.find((a) => a.id === selectedAction);
      setPendingAction({ type: selectedAction, employee: selectedEmployee, formData, actionConfig });
      setShowActionModal(false);
      setShowConfirmModal(true);
    },
    [selectedAction, selectedEmployee, formData, validateForm]
  );

  const handleConfirmAction = useCallback(async () => {
    setIsSubmitting(true);
    try {
      const isSalaryRelated = ["salary_change", "promote", "demote", "rehire"].includes(
        pendingAction.type
      );
      const oldSalary = selectedEmployee.netSalary || 0;
      const newSalary = formData.newSalary ? parseFloat(formData.newSalary) : oldSalary;
      const salaryChange = newSalary - oldSalary;
      const changePercent = oldSalary > 0 ? (salaryChange / oldSalary) * 100 : 0;
      const oldGrossSalary = selectedEmployee.grossSalary || 0;
      const newGrossSalary = formData.newGrossSalary
        ? parseFloat(formData.newGrossSalary)
        : oldGrossSalary;

      const effectiveDate = (pendingAction.type === "suspend" && formData.suspensionStartDate)
        ? formData.suspensionStartDate
        : (pendingAction.type === "terminate" && formData.lastWorkingDay)
        ? formData.lastWorkingDay
        : formData.effectiveDate;

      const actionData = {
        employeeId: selectedEmployee.id,
        infinityCode: selectedEmployee.infinityCode,
        employeeName: getEmployeeName(selectedEmployee),
        actionType: pendingAction.type,
        effectiveDate: effectiveDate,
        reason: formData.reason,
        approverNotes: formData.approverNotes,
        oldPosition: selectedEmployee.position ?? selectedEmployee.employeeType,
        newPosition: formData.newPosition || selectedEmployee.position || selectedEmployee.employeeType,
        oldDepartment: selectedEmployee.department?.name ?? selectedEmployee.location ?? selectedEmployee.employeeType,
        newDepartment: formData.newDepartment || selectedEmployee.department?.name || selectedEmployee.location || selectedEmployee.employeeType,
        oldSalary,
        newSalary: isSalaryRelated ? newSalary : oldSalary,
        oldGrossSalary,
        newGrossSalary: isSalaryRelated ? newGrossSalary : oldGrossSalary,
        salaryChange: isSalaryRelated ? salaryChange : 0,
        changePercent: isSalaryRelated ? changePercent : 0,
        oldReportsTo: selectedEmployee.firstLineManagerName,
        newReportsTo: formData.reportsTo || selectedEmployee.firstLineManagerName,
        performanceRating: formData.performanceRating,
        terminationType: formData.terminationType,
        terminationReason: formData.terminationReason,
        lastWorkingDay: formData.lastWorkingDay,
        severanceAmount: formData.severanceAmount,
        eligibleForRehire: formData.eligibleForRehire,
        suspensionStartDate: formData.suspensionStartDate,
        suspensionEndDate: formData.suspensionEndDate,
        suspensionType: formData.suspensionType,
        suspensionReason: formData.suspensionReason,
        trainingProgram: formData.trainingProgram,
        trainingStartDate: formData.trainingStartDate,
        trainingEndDate: formData.trainingEndDate,
        trainingCost: formData.trainingCost,
        trainingObjectives: formData.trainingObjectives,
        reviewScore: formData.reviewScore,
        strengths: formData.strengths,
        improvements: formData.improvements,
        nextReviewDate: formData.nextReviewDate,
        contractType: formData.contractType,
        contractStartDate: formData.contractStartDate,
        contractEndDate: formData.contractEndDate,
        contractTerms: formData.contractTerms,
        rotationDepartment: formData.rotationDepartment,
        rotationRole: formData.rotationRole,
        rotationDuration: formData.rotationDuration,
        rotationObjectives: formData.rotationObjectives,
        performedBy: String(
          profile?.infinityCode || localStorage.getItem("infinityCode")
        ),
        status: pendingAction.actionConfig.requiresApproval ? "pending" : "approved",
        priority: formData.priority || "medium",
        confidential: formData.confidential || false,
        oldData: {
          position: selectedEmployee.position ?? selectedEmployee.employeeType,
          department: selectedEmployee.department?.name ?? selectedEmployee.employeeType,
          salary: oldSalary,
          reportsTo: selectedEmployee.firstLineManagerName,
          performance: selectedEmployee.performance,
        },
        newData: {
          position: formData.newPosition || selectedEmployee.position || selectedEmployee.employeeType,
          department: formData.newDepartment || selectedEmployee.department?.name || selectedEmployee.employeeType,
          salary: newSalary,
          reportsTo: formData.reportsTo || selectedEmployee.firstLineManagerName,
          performance: formData.performanceRating || selectedEmployee.performance,
        },
      };

      await employeeActionsAPI.createAction(actionData);
      setRefreshTrigger((prev) => prev + 1);
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("employee-data-updated", { detail: { actionType: actionData.actionType } }));
      }
      await Promise.all([
        fetchEmployees(false),
        fetchActionHistory(),
        fetchPendingActions(),
        fetchStats(),
        fetchDepartments(),
      ]);

      const actionLabel =
        t[pendingAction.actionConfig.labelKey] ||
        pendingAction.actionConfig.labelKey;
      let msg = `${actionLabel} ${t.actionSubmittedSuccess} ${getEmployeeName(selectedEmployee)}.`;
      if (isSalaryRelated && salaryChange !== 0) {
        msg +=
          salaryChange > 0
            ? ` ${t.salaryWillIncrease} ${Math.abs(salaryChange).toLocaleString()} (+${changePercent.toFixed(1)}%).`
            : ` ${t.salaryWillDecrease} ${Math.abs(salaryChange).toLocaleString()} (${changePercent.toFixed(1)}%).`;
        msg += pendingAction.actionConfig.requiresApproval
          ? ` ${t.changesAppliedOnApproval}`
          : ` ${t.changesAppliedImmediately}`;
      } else if (pendingAction.actionConfig.requiresApproval) {
        msg += ` ${t.awaitingApproval}`;
      }

      setSuccessMsg(msg);
      setShowConfirmModal(false);
      setPendingAction(null);
      setSelectedEmployee(null);
      setSelectedAction("");
      setFormData(EMPTY_FORM);
      setTimeout(() => setSuccessMsg(""), 7000);
    } catch (err) {
      setErrorMsg(
        `${t.errorProcessingAction} ${err?.response?.data?.message || err?.message || t.pleaseTryAgain}`
      );
      setTimeout(() => setErrorMsg(""), 10000);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    selectedEmployee, pendingAction, formData,
    fetchEmployees, fetchActionHistory, fetchPendingActions, fetchStats, fetchDepartments,
    t, getEmployeeName,
  ]);

  const openActionModal = useCallback(
    (employee) => {
      setSelectedEmployee(employee);
      setSelectedAction("");
      setIsDropdownOpen(false);
      setFormData({
        ...EMPTY_FORM,
        newPosition: employee.position ?? employee.employeeType ?? "",
        newDepartment: employee.department?.name ?? employee.employeeType ?? "",
        newSalary: employee.netSalary || "",
        newGrossSalary: employee.grossSalary || "",
        effectiveDate: new Date().toISOString().split("T")[0],
        reportsTo: employee.firstLineManagerName || "",
      });
      setErrorMsg("");
      setShowActionModal(true);
    },
    []
  );

  // ── Effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    // Fetch workflow definitions once so approval-chain labels are dynamic
    const fetchWorkflowDefinitions = async () => {
      try {
        const clientName =
          profile?.clientName || localStorage.getItem("clientName");
        const workingCompany =
          profile?.workingCompany || localStorage.getItem("workingCompany");
        if (!clientName || !workingCompany) return;
        const defs = await employeeActionsAPI.listWorkflowDefinitions(
          clientName,
          workingCompany
        );
        setWorkflowDefinitions(Array.isArray(defs) ? defs : []);
      } catch {
        setWorkflowDefinitions([]);
      }
    };

    Promise.all([
      fetchClients(),
      fetchEmployees(true),
      fetchActionHistory(),
      fetchPendingActions(),
      fetchWorkflowDefinitions(),
      fetchDepartments(),
    ]);
  }, []);

  // Removed loadImages effect to improve performance as requested by user

  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => fetchEmployees(false), 300);
    return () => clearTimeout(debounceTimerRef.current);
  }, [selectedClient, filterDepartment, filterPerformance, searchTerm, refreshTrigger, fetchEmployees]);

  useEffect(() => {
    if (!isLoading && employees.length > 0) fetchStats();
  }, [employees, fetchStats, isLoading]);

  useEffect(() => {
    const target = window.__notificationTarget;
    if (
      target?.type === "EMPLOYEE_ACTION" &&
      pendingActions.length > 0 &&
      canViewPendingApprovals
    ) {
      setShowPendingModal(true);
      window.__notificationTarget = null;
    }
  }, [pendingActions, canViewPendingApprovals]);

  // Notification deep-link (?actionId=...): open the pending-approvals modal
  // with the targeted change highlighted (when it's still pending).
  const focusConsumedRef = useRef(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const focusActionId = params.get("actionId");
    if (!focusActionId || focusConsumedRef.current) return;
    if (pendingActions.length > 0 && canViewPendingApprovals) {
      const match = pendingActions.find((a) => Number(a.id) === Number(focusActionId));
      focusConsumedRef.current = true;
      if (match) setHighlightActionId(match.id);
      setShowPendingModal(true);
    }
  }, [pendingActions, canViewPendingApprovals]);

  // ── Derived lists ──────────────────────────────────────────────────────────
  const companies = useMemo(
    () => [...new Set(employees.map((e) => e.workingCompany).filter(Boolean))].sort(),
    [employees]
  );

  const performances = useMemo(
    () => [...new Set(employees.map((e) => e.performance).filter(Boolean))],
    [employees]
  );

  const statuses = useMemo(
    () => [...new Set(employees.map((e) => e.employeeState).filter(Boolean))],
    [employees]
  );

  const sortedEmployees = useMemo(() => {
    return [...employees].sort((a, b) => {
      let aVal, bVal;
      switch (sortBy) {
        case "name":
          aVal = (language === "ar" ? a.arabicName || a.sr : a.sr || a.arabicName || "").toLowerCase();
          bVal = (language === "ar" ? b.arabicName || b.sr : b.sr || b.arabicName || "").toLowerCase();
          break;
        case "salary":
          aVal = a.netSalary || 0; bVal = b.netSalary || 0; break;
        case "joinDate":
          aVal = new Date(a.hiringDate || 0); bVal = new Date(b.hiringDate || 0); break;
        default: return 0;
      }
      if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
      if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }, [employees, sortBy, sortOrder, language]);

  const normalizeSearchText = useCallback((str) => {
    if (!str) return "";
    return String(str)
      .toLowerCase()
      .trim()
      .replace(/[أإآ]/g, "ا")
      .replace(/ة/g, "ه")
      .replace(/ى/g, "ي")
      .replace(/[\u064B-\u0652]/g, "");
  }, []);

  const filteredEmployees = useMemo(() => {
    const rawSearch = (searchTerm || "").trim();
    const normalizedTerm = normalizeSearchText(rawSearch);
    const strippedTerm = normalizedTerm.replace(/^0+/, "");

    return sortedEmployees.filter((emp) => {
      let matchSearch = true;
      if (normalizedTerm) {
        const srName = normalizeSearchText(emp.sr);
        const arName = normalizeSearchText(emp.arabicName);
        const email = normalizeSearchText(emp.email);
        const pos = normalizeSearchText(emp.position || emp.employeeType);
        const dept = normalizeSearchText(emp.department?.name);
        const company = normalizeSearchText(emp.workingCompany);
        const client = normalizeSearchText(emp.clientName);
        const loc = normalizeSearchText(emp.location);
        const code = String(emp.infinityCode || "").trim().toLowerCase();
        const strippedCode = code.replace(/^0+/, "");
        const phone = normalizeSearchText(emp.phoneNumber);
        const natId = normalizeSearchText(emp.nationalId);

        matchSearch =
          srName.includes(normalizedTerm) ||
          arName.includes(normalizedTerm) ||
          email.includes(normalizedTerm) ||
          pos.includes(normalizedTerm) ||
          dept.includes(normalizedTerm) ||
          company.includes(normalizedTerm) ||
          client.includes(normalizedTerm) ||
          loc.includes(normalizedTerm) ||
          code.includes(normalizedTerm) ||
          (strippedTerm.length > 0 && strippedCode.includes(strippedTerm)) ||
          phone.includes(normalizedTerm) ||
          natId.includes(normalizedTerm);
      }

      const matchDept = filterDepartment === "all" || (emp.department?.name ?? emp.employeeType) === filterDepartment;
      const matchCompany = filterCompany === "all" || emp.workingCompany === filterCompany;
      const matchPerf = filterPerformance === "all" || emp.performance === filterPerformance;
      const matchStatus = filterStatus === "all" || emp.employeeState === filterStatus;

      return matchSearch && matchDept && matchCompany && matchPerf && matchStatus;
    });
  }, [sortedEmployees, searchTerm, filterDepartment, filterCompany, filterPerformance, filterStatus, normalizeSearchText]);

  // ── Form handlers ──────────────────────────────────────────────────────────
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: type === "checkbox" ? checked : value };
      if (name === "suspensionStartDate" || name === "lastWorkingDay") {
        next.effectiveDate = value;
      }
      return next;
    });
    setErrorMsg("");
  }, []);

  const handleDropdownChange = useCallback((fieldName, value) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
    setErrorMsg("");
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────────
  // Permission check: user must have access to changesPage module
  // Note: Admins and superadmins get automatic access
  // ── Render ─────────────────────────────────────────────────────────────────
  // Permission check:
  // - superadmin/admin → دائماً مسموح
  // - manager في manager view → مسموح زي باقي صفحات المانجر
  // - في admin view → لازم يكون عنده موديول changesPage
  const role = (profile?.role || localStorage.getItem("role") || "").toLowerCase();
  const isManagerView = !isAdminView; // isAdminView معرّفة فوق من viewType
  const canAccessChanges =
    role === "superadmin" ||
    role === "admin" ||
    isManagerView ||
    hasModuleAccess("changesPage");

  if (!canAccessChanges) {
    return (
      <div style={{ padding: "40px 20px", textAlign: "center" }}>
        <AlertCircle size={48} style={{ color: "#ef4444", marginBottom: "20px" }} />
        <h2 style={{ color: theme === "dark" ? "#f1f5f9" : "#0f172a", marginBottom: "10px" }}>
          {language === "ar" ? "لا توجد صلاحيات" : "Access Denied"}
        </h2>
        <p style={{ color: theme === "dark" ? "#94a3b8" : "#64748b" }}>
          {language === "ar"
            ? "ليس لديك صلاحية للوصول إلى صفحة تغييرات الموظفين"
            : "You don't have permission to access the Changes page"}
        </p>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // INITIAL SKELETON — only until the first successful load
  // ═══════════════════════════════════════════════════════════
  if (!pageReady && isLoading) {
    return (
      <div>
        <SkeletonStyles theme={theme} />
        <LoadingPill
          text={isRTL ? "جارٍ تحميل بيانات الموظفين..." : "Loading employee actions information..."}
          theme={theme}
          isRTL={isRTL}
        />

        {/* Header skeleton */}
        <div
          style={{
            background: theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.9)",
            padding: "32px",
            borderRadius: "24px",
            border: theme === "dark" ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.05)",
            marginBottom: "32px",
            direction: isRTL ? "rtl" : "ltr",
          }}
        >
          <SkeletonBox width="220px" height="30px" style={{ marginBottom: "12px" }} />
          <SkeletonBox width="320px" height="18px" />
        </div>

        {/* Stats skeleton */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: "20px",
            marginBottom: "32px",
          }}
        >
          {[1, 2, 3, 4, 5, 6, 7].map((i) => (
            <SkeletonStatCard key={i} cardStyle={styles.statCard} />
          ))}
        </div>

        {/* Filters skeleton */}
        <div style={styles.filterCard}>
          <SkeletonBox width="220px" height="24px" style={{ marginBottom: "20px" }} />
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <SkeletonBox width="200px" height="40px" style={{ borderRadius: "10px" }} />
            <SkeletonBox width="180px" height="40px" style={{ borderRadius: "10px" }} />
            <SkeletonBox width="160px" height="40px" style={{ borderRadius: "10px" }} />
            <SkeletonBox width="160px" height="40px" style={{ borderRadius: "10px" }} />
          </div>
        </div>

        {/* Employee table skeleton */}
        <div style={styles.filterCard}>
          <SkeletonBox width="220px" height="24px" style={{ marginBottom: "20px" }} />
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <SkeletonBox key={i} width="100%" height="52px" style={{ borderRadius: "10px" }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <style>{`
        @keyframes slideInDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
      <div
        style={{
          background: theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.9)",
          padding: "32px",
          borderRadius: "24px",
          border: theme === "dark" ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.05)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          boxShadow: theme === "dark"
            ? "0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)"
            : "0 8px 32px rgba(0,0,0,0.1)",
          animation: "slideInDown 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)",
          transition: "all 0.3s ease",
          marginBottom: "32px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: "24px",
          direction: isRTL ? "rtl" : "ltr",
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: "28px",
              fontWeight: 700,
              color: theme === "dark" ? "#f1f5f9" : "#1e293b",
              letterSpacing: "-0.5px",
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <Users size={28} />
            {t.title}
          </h1>
          <p
            style={{
              margin: "12px 0 0 0",
              fontSize: "15px",
              fontWeight: 500,
              color: theme === "dark" ? "#cbd5e1" : "#64748b",
            }}
          >
            {t.Subtitles || "Manage employee actions and career movements"}
          </p>
        </div>
      </div>

      {/* Stats — fluid: one row on desktop, wraps on phones instead of
          squeezing 7 clipped columns into the viewport. */}
      <SharedStatsGrid
        stats={stats}
        styles={styles}
        gridStyle={{
          gridTemplateColumns: "repeat(auto-fit, minmax(min(150px, 100%), 1fr))",
        }}
      />

      {/* Alerts */}
      {successMsg && (
        <div style={{ ...styles.alertBox, ...styles.successAlert }}>
          <Check size={22} />
          <span style={{ fontWeight: "700" }}>{successMsg}</span>
        </div>
      )}
      {errorMsg && (
        <div style={{ ...styles.alertBox, ...styles.errorAlert }}>
          <AlertCircle size={22} />
          <span style={{ fontWeight: "700" }}>{errorMsg}</span>
        </div>
      )}

      {/* Filters + Table */}
      <EmployeeTable
        t={t}
        styles={styles}
        theme={theme}
        isRTL={isRTL}
        language={language}
        isLoading={isLoading}
        employees={employees}
        filteredEmployees={filteredEmployees}
        clients={clients}
        companies={companies}
        departments={departments}
        performances={performances}
        statuses={statuses}
        userRole={userRole}
        selectedClient={selectedClient}
        setSelectedClient={setSelectedClient}
        filterCompany={filterCompany}
        setFilterCompany={setFilterCompany}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        filterDepartment={filterDepartment}
        setFilterDepartment={setFilterDepartment}
        filterPerformance={filterPerformance}
        setFilterPerformance={setFilterPerformance}
        filterStatus={filterStatus}
        setFilterStatus={setFilterStatus}
        sortBy={sortBy}
        setSortBy={setSortBy}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        employeeImages={employeeImages}
        hoveredActionBtn={hoveredActionBtn}
        setHoveredActionBtn={setHoveredActionBtn}
        getEmployeeName={getEmployeeName}
        getInitials={getInitials}
        hasFeatureAccess={hasFeatureAccess}
        isColumnVisible={isColumnVisible}
        canViewPendingApprovals={canViewPendingApprovals}
        onOpenHistory={() => setShowHistoryModal(true)}
        onOpenPending={() => setShowPendingModal(true)}
        onAction={openActionModal}
        onHistory={handleOpenEmployeeHistory}
        onClearFilters={() => {
          setSearchTerm("");
          setFilterCompany("all");
          setFilterDepartment("all");
          setFilterPerformance("all");
          setFilterStatus("all");
          setSelectedClient("all");
        }}
      />

      {/* Action Modal */}
      {showActionModal && selectedEmployee && (
        <ActionModal
          t={t}
          styles={styles}
          theme={theme}
          isRTL={isRTL}
          language={language}
          selectedEmployee={selectedEmployee}
          selectedAction={selectedAction}
          setSelectedAction={setSelectedAction}
          isDropdownOpen={isDropdownOpen}
          setIsDropdownOpen={setIsDropdownOpen}
          formData={formData}
          setFormData={setFormData}
          errorMsg={errorMsg}
          setErrorMsg={setErrorMsg}
          departments={departments}
          departmentManagers={departmentManagers}
          getEmployeeName={getEmployeeName}
          handleInputChange={handleInputChange}
          handleDropdownChange={handleDropdownChange}
          handleSubmit={handleSubmit}
          permissions={permissions}
          isAdminView={isAdminView}
          isAdminRole={isAdminRole}
          hasFeatureAccess={hasFeatureAccess}
          ACTION_TYPES={ACTION_TYPES}
          ACTION_FEATURE_MAP={ACTION_FEATURE_MAP}
          workflowDefinitions={workflowDefinitions}
          onClose={() => {
            setShowActionModal(false);
            setIsDropdownOpen(false);
          }}
        />
      )}

      {/* Confirm Modal */}
      {showConfirmModal && pendingAction && (
        <ConfirmModal
          t={t}
          styles={styles}
          theme={theme}
          isRTL={isRTL}
          language={language}
          pendingAction={pendingAction}
          isSubmitting={isSubmitting}
          getEmployeeName={getEmployeeName}
          workflowDefinitions={workflowDefinitions}
          onConfirm={handleConfirmAction}
          onClose={() => setShowConfirmModal(false)}
        />
      )}

      {/* History Modal */}
      {showHistoryModal && (
        <HistoryModal
          t={t}
          styles={styles}
          theme={theme}
          isRTL={isRTL}
          language={language}
          actionHistory={actionHistory}
          ACTION_TYPES={ACTION_TYPES}
          hasFeatureAccess={hasFeatureAccess}
          onClose={() => setShowHistoryModal(false)}
        />
      )}

      {/* Pending Approvals Modal */}
      {showPendingModal && (
        <PendingApprovalsModal
          t={t}
          styles={styles}
          theme={theme}
          isRTL={isRTL}
          language={language}
          pendingActions={pendingActions}
          pendingWorkflows={pendingWorkflows}
          isSubmitting={isSubmitting}
          permissions={permissions}
          isAdminView={isAdminView}
          isAdminRole={isAdminRole}
          ACTION_TYPES={ACTION_TYPES}
          profile={profile}
          workflowDefinitions={workflowDefinitions}
          highlightActionId={highlightActionId}
          onApprove={handleApproveAction}
          onReject={(action) => {
            setRejectingActionId(action.id);
            setRejectingActionData(action);
            setRejectionReason("");
            setShowRejectModal(true);
          }}
          onClose={() => {
            setShowPendingModal(false);
            setHighlightActionId(null);
          }}
        />
      )}

      {/* Employee History Modal */}
      {showEmployeeHistoryModal && selectedEmployeeHistory && (
        <EmployeeHistoryModal
          t={t}
          styles={styles}
          theme={theme}
          isRTL={isRTL}
          language={language}
          employee={selectedEmployeeHistory}
          historyData={employeeHistoryData}
          getEmployeeName={getEmployeeName}
          ACTION_TYPES={ACTION_TYPES}
          onClose={() => setShowEmployeeHistoryModal(false)}
        />
      )}

      {/* Reject Modal */}
      {showRejectModal && rejectingActionData && (
        <RejectModal
          t={t}
          styles={styles}
          theme={theme}
          isRTL={isRTL}
          language={language}
          actionData={rejectingActionData}
          rejectionReason={rejectionReason}
          setRejectionReason={setRejectionReason}
          isSubmitting={isSubmitting}
          errorMsg={errorMsg}
          setErrorMsg={setErrorMsg}
          ACTION_TYPES={ACTION_TYPES}
          workflowDefinitions={workflowDefinitions}
          onConfirm={() => {
            if (!rejectionReason.trim()) {
              setErrorMsg(t.reasonRequired || "Rejection reason is required");
              setTimeout(() => setErrorMsg(""), 3000);
              return;
            }
            handleRejectAction(rejectingActionId, rejectionReason);
          }}
          onClose={() => {
            setShowRejectModal(false);
            setRejectingActionId(null);
            setRejectingActionData(null);
            setRejectionReason("");
          }}
        />
      )}

      {/* Global CSS */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes dropdownSlideIn {
          from { opacity: 0; transform: translateY(-12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .fade-in-alert { animation: fadeIn 0.4s ease; }
        .enhanced-input-focus:focus {
          border-color: #2e7cba;
          box-shadow: 0 0 0 4px rgba(59,130,246,0.2);
          transform: scale(1.01);
        }
        .enhanced-button-hover:hover {
          transform: translateY(-2px);
          box-shadow: 0 14px 35px rgba(59,130,246,0.4);
        }
        .enhanced-button-hover:active { transform: translateY(0); }
        .action-btn-hover:hover { transform: scale(1.05); box-shadow: 0 10px 30px rgba(59,130,246,0.5); }
        .table-row-hover:hover {
          background: ${theme === "dark" ? "rgba(59,130,246,0.08)" : "rgba(59,130,246,0.05)"} !important;
        }
        .timeline-card-hover:hover {
          transform: translateX(${isRTL ? "-4px" : "4px"});
          box-shadow: ${theme === "dark" ? "0 8px 24px rgba(0,0,0,0.4)" : "0 8px 24px rgba(0,0,0,0.1)"};
        }
        .employeeAvatar img ~ .avatar-initials { display: none !important; }
        ::-webkit-scrollbar { width: 10px; height: 10px; }
        ::-webkit-scrollbar-track {
          background: ${theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"};
          border-radius: 10px;
        }
        ::-webkit-scrollbar-thumb {
          background: ${theme === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)"};
          border-radius: 10px;
        }
      `}</style>
    </div>
  );
};

export default EmployeeActionsPage;